import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Page publique de désinscription newsletter (lien dans les emails).
 * Lit ?email= dans l'URL, propose la confirmation 1 clic, ou laisse l'utilisateur
 * saisir son adresse manuellement.
 */
export default function NewsletterUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setErrorMsg("Email invalide");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/newsletter/unsubscribe`, { email });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Une erreur est survenue. Réessaye dans un instant.");
    }
  };

  // Auto-submit si l'email vient de l'URL et est valide
  useEffect(() => {
    const urlEmail = searchParams.get("email");
    if (!urlEmail || !urlEmail.includes("@")) return;
    // micro-délai pour permettre le rendu initial
    const t = setTimeout(async () => {
      setStatus("loading");
      setErrorMsg("");
      try {
        await axios.post(`${API}/newsletter/unsubscribe`, { email: urlEmail });
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMsg(err.response?.data?.detail || "Une erreur est survenue. Réessaye dans un instant.");
      }
    }, 200);
    return () => clearTimeout(t);
  }, [searchParams]);

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-24 bg-[#1B132B]" data-testid="newsletter-unsubscribe-page">
      <div className="max-w-md w-full bg-[#261B3B] border border-white/10 rounded-2xl p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="w-6 h-6 text-[#D926A9]" />
          <h1 className="font-serif text-2xl text-white">Désinscription newsletter</h1>
        </div>

        {status === "success" ? (
          <div className="text-center py-4" data-testid="unsubscribe-success">
            <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
            <p className="text-white text-lg mb-2 font-serif">C'est confirmé.</p>
            <p className="text-[#B59ED1] text-sm mb-6">
              <strong className="text-white">{email}</strong> ne recevra plus la newsletter de Bilan 2 Vie. Tu peux te réinscrire à tout moment depuis ton profil.
            </p>
            <Link to="/" className="inline-block text-[#00E5FF] hover:underline text-sm">
              Retour à l'accueil
            </Link>
          </div>
        ) : (
          <>
            <p className="text-[#E2D5F8] text-sm leading-relaxed mb-6">
              Confirme ton adresse pour ne plus recevoir la newsletter (ateliers, soirées spirituelles, offres). Tu pourras toujours te réinscrire plus tard.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-[#B59ED1] text-sm">Adresse email</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.fr"
                  className="bg-[#1B132B] border-white/10 mt-1 text-white"
                  data-testid="unsubscribe-email"
                  disabled={status === "loading"}
                />
              </div>
              {status === "error" && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="unsubscribe-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <Button
                type="submit"
                disabled={status === "loading"}
                className="w-full gradient-primary"
                data-testid="unsubscribe-submit"
              >
                {status === "loading" ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Désinscription en cours…</>
                ) : (
                  "Me désinscrire"
                )}
              </Button>
              <p className="text-center text-xs text-[#B59ED1]">
                <Link to="/" className="hover:underline">Retour à l'accueil</Link>
              </p>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
